import React, { Component, Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography/Typography';
import 'firebase/auth';
import 'firebase/database';
import IconButton from '@material-ui/core/IconButton';
import BackArrow from '@material-ui/icons/ArrowBackIos';
import AppBar from '@material-ui/core/AppBar/AppBar';
import Toolbar from '@material-ui/core/Toolbar/Toolbar';
import PropTypes from 'prop-types';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';

import './ImportRecipients.css';
import ConfirmationDialog from '../../Components/ConfirmationDialog/ConfirmationDialog';
import { db, getUser, redirectToRoot } from '../../utilities/firebase-config';
import styles from './ImportRecipients.style';

const defaultConfirmationDialog = {
  display: false,
  message: '',
  messages: [],
  negativeAction: () => {},
  negativeText: '',
  positiveAction: () => {},
  positiveText: '',
  title: '',
};

const initialState = {
  confirmationDialog: defaultConfirmationDialog,
  importNumbers: '',
  snackbar: {
    open: false,
    text: '',
  },
  submitted: false,
  submitting: false,
  uid: null,
};

const List = ({ items }) => (
  <ul style={ { paddingInlineStart: 5 } }>
    { items.map(item => <li key={ item }>{item}</li>) }
  </ul>
);

List.propTypes = {
  items: PropTypes.array.isRequired,
};

class ImportRecipients extends Component {
  state = initialState;

  async componentDidMount() {
    const { history } = this.props;
    const user = await getUser();

    if (!user || !user.profile || user.profile.permissions !== 'Administrator') {
      redirectToRoot(history);
    }
  }

  goBack = () => {
    const { history: { goBack } } = this.props;
    goBack();
  };

  routeToRecipients = (importRecipientsKey) => {
    const { history: { replace } } = this.props;
    const location = {
      pathname: '/recipients',
      search: `importRecipientsKey=${importRecipientsKey}`,
    };

    replace(location);
  };

  changeField = (event) => {
    this.setState({
      [event.target.name]: event.target.value,
    });
  };

  submit = (event) => {
    const toBeImported = [];
    const toBeImportedDisplay = [];
    const toBeSkipped = [];
    const toBeSkippedDisplay = [];
    let duplicatesIgnored = 0;
    const { importNumbers } = this.state;

    event.preventDefault();

    this.setState({
      submitting: true,
    });

    window.scrollTo(0, 0);

    // Generate array of all the inputs, breaking on spaces, newlines
    const importNumbersArray = importNumbers
      .replace(/\s+/g, ',')
      .replace(/,+/g, ',')
      .replace(/^,/, '')
      .replace(/,$/, '')
      .split(',');

    importNumbersArray.map((number) => {
      const cleanedNumber = number
        .replace(/^\+/gi, '')
        .replace(/^1/gi, '')
        .replace(/[^0-9+]/gi, '');

      if (cleanedNumber.length === 10) {
        const formatted = `+1${cleanedNumber}`;

        // Do not import the same number repeatedly
        if (toBeImported.indexOf(formatted) === -1) {
          toBeImported.push(formatted);
          toBeImportedDisplay.push(number);
        } else {
          duplicatesIgnored += 1;
        }
      } else {
        toBeSkipped.push(cleanedNumber);
        toBeSkippedDisplay.push(number);
      }

      return null;
    });

    // If there are any problems, alert user and prevent submitting text
    if (!toBeImported.length) {
      this.setState({
        confirmationDialog: {
          ...defaultConfirmationDialog,
          display: true,
          messages: ['Please provide a valid list of recipients to import.'],
          negativeAction: this.closeConfirmationDialog,
          negativeText: 'Okay',
          title: 'Action Required',
        },
        submitting: false,
      });

      return;
    }

    this.setState({
      confirmationDialog: {
        ...defaultConfirmationDialog,
        display: true,
        message: this.generateConfirmationMessage(
          toBeImportedDisplay, toBeSkippedDisplay, duplicatesIgnored,
        ),
        negativeAction: this.closeConfirmationDialog,
        negativeText: 'No',
        positiveAction: this.processImportRecipients.bind(null, toBeImported),
        positiveText: 'Yes',
        title: 'Confirm',
      },
    });
  };

  reset = () => {
    window.scrollTo(0, 0);
    this.setState(initialState);
  };

  closeConfirmationDialog = () => {
    this.setState({
      confirmationDialog: defaultConfirmationDialog,
      submitting: false,
    });

    setTimeout(() => {
      window.scrollTo(0, 0);
    });
  };

  processImportRecipients = (toBeImported) => {
    this.setState({
      confirmationDialog: {
        ...defaultConfirmationDialog,
        display: false,
      },
      submitting: true,
    });

    db.ref('importRecipients').push(toBeImported)
      .then((response) => {
        const importRecipientsKey = response.key;
        this.setState({
          importNumbers: '',
        });
        setTimeout(() => {
          this.routeToRecipients(importRecipientsKey);
        });
      })
      .finally(() => {
        this.setState({
          submitting: false,
        });
      })
      .catch(() => {
        this.setState({
          snackbar: {
            open: true,
            text: 'Failed to import numbers',
          },
        });
      });
  };

  generateConfirmationMessage = (toBeImportedDisplay, toBeSkippedDisplay, duplicatesIgnored) => (
    <span style={ { display: 'block' } }>

      {
        toBeImportedDisplay.length > 0 && (
          <Fragment>
            <Typography gutterBottom variant="h6" component="span">
              Numbers To Be Imported
            </Typography>
            <span style={ { display: 'block', marginBottom: 8 } }>
              <List items={ toBeImportedDisplay } />
            </span>
          </Fragment>
        )
      }

      {
        toBeSkippedDisplay.length > 0 && (
          <Fragment>
            <Typography gutterBottom variant="h6" component="span">
              Numbers To Be Skipped
            </Typography>
            <span style={ { display: 'block', marginBottom: 8 } }>
              <List items={ toBeSkippedDisplay } />
            </span>
          </Fragment>
        )
      }

      {
        duplicatesIgnored > 0 && (
          <Fragment>
            <Typography gutterBottom variant="h6" component="span">
              Duplicates Ignored:&nbsp;
              { duplicatesIgnored }
            </Typography>
          </Fragment>
        )
      }
    </span>
  );

  closeSnackbar = () => {
    this.setState({
      snackbar: {
        open: false,
        text: '',
      },
    });
  }

  render() {
    const { classes } = this.props;
    const {
      importNumbers,
      confirmationDialog,
      snackbar,
      submitting,
    } = this.state;

    return (
      <div className="ImportRecipients container">
        <AppBar position="fixed" color="primary">
          <Toolbar>
            <IconButton className={ classes.menuButton } color="inherit" aria-label="Menu" onClick={ this.goBack }>
              <BackArrow />
            </IconButton>

            <Typography variant="h6" color="inherit">
              Import Numbers
            </Typography>
          </Toolbar>
        </AppBar>

        <Snackbar
          open={ snackbar.open }
          autoHideDuration={ 7500 }
          ContentProps={ {
            'aria-describedby': 'message-id',
          } }
          message={ <span id="message-id">{ snackbar.text }</span> }
          action={ [
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              className={ classes.close }
              onClick={ this.closeSnackbar }
            >
              <CloseIcon />
            </IconButton>,
          ] }
        />

        <ConfirmationDialog
          open={ confirmationDialog.display }
          negativeAction={ confirmationDialog.negativeAction }
          negativeText={ confirmationDialog.negativeText }
          positiveAction={ confirmationDialog.positiveAction }
          positiveText={ confirmationDialog.positiveText }
          title={ confirmationDialog.title }
          message={ confirmationDialog.message }
          messages={ confirmationDialog.messages }
        />
        <form onSubmit={ this.submit } name="send-page">

          <TextField
            className={ classes.textArea }
            id="importNumbers"
            label="New Phone Numbers"
            placeholder="Enter phone numbers to import, one per line."
            name="importNumbers"
            fullWidth
            multiline
            rows={ 10 }
            margin="normal"
            variant="outlined"
            value={ importNumbers }
            onChange={ this.changeField }
            InputLabelProps={ {
              shrink: true,
            } }
          />

          <Button
            variant="outlined"
            color="primary"
            className={ classes.button }
            onClick={ this.submit }
            type="submit"
            disabled={ submitting }
          >
            Import Numbers
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            className={ classes.button }
            onClick={ this.reset }
          >
            Reset
          </Button>
        </form>
      </div>
    );
  }
}

ImportRecipients.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withStyles(styles)(ImportRecipients);
